import type { IssueType, Project, Workflow } from '@servicetim/shared';
import { useIssueTypes, useReorderIssueTypes, useWorkflows } from '@servicetim/ui/api';
import { Badge, Button, type ColumnDef, DataTable } from '@servicetim/ui/components';
import { Pencil, Plus, Trash2 } from 'lucide-react';
import { useMemo, useState } from 'react';
import { moveItem } from '../lib/list-utils';
import { notifyError } from '../lib/notify';
import { ReorderButtons } from '../ui/reorder-buttons';
import { DeleteIssueTypeDialog } from './delete-issue-type-dialog';
import { IssueTypeDialog } from './issue-type-dialog';

/** Ordered issue types with up/down reordering, create/edit dialog and delete-with-move. */
export function IssueTypesTable({ project }: { project: Project }) {
  const projectId = project.id;
  const issueTypes = useIssueTypes(projectId);
  const workflows = useWorkflows(projectId);
  const reorder = useReorderIssueTypes(projectId);
  const [editing, setEditing] = useState<IssueType | null | 'new'>(null);
  const [deleting, setDeleting] = useState<IssueType | null>(null);

  const workflowById = useMemo(
    () => new Map<string, Workflow>((workflows.data ?? []).map((w) => [w.id, w])),
    [workflows.data],
  );
  const rows = issueTypes.data ?? [];

  const move = (from: number, to: number) => {
    const ids = moveItem(rows, from, to).map((t) => t.id);
    reorder.mutate({ ids }, { onError: (e) => notifyError(e, 'Could not reorder') });
  };

  // biome-ignore lint/correctness/useExhaustiveDependencies: handlers and mutation resets are stable for the component lifetime
  const columns = useMemo<ColumnDef<IssueType>[]>(
    () => [
      {
        id: 'order',
        header: 'Order',
        size: 90,
        enableSorting: false,
        cell: ({ row }) => (
          <ReorderButtons
            label={row.original.name}
            disabled={reorder.isPending}
            onUp={row.index > 0 ? () => move(row.index, row.index - 1) : undefined}
            onDown={row.index < rows.length - 1 ? () => move(row.index, row.index + 1) : undefined}
          />
        ),
      },
      {
        accessorKey: 'name',
        header: 'Name',
        enableSorting: false,
        cell: ({ row }) => (
          <div className="flex items-center gap-2">
            {row.original.icon ? (
              <span aria-hidden="true" className="text-base leading-none">
                {row.original.icon}
              </span>
            ) : null}
            <span className="font-medium text-fg">{row.original.name}</span>
            {row.original.isSubtask ? <Badge variant="secondary">Sub-task</Badge> : null}
          </div>
        ),
      },
      {
        accessorKey: 'description',
        header: 'Description',
        enableSorting: false,
        cell: ({ getValue }) => (
          <span className="line-clamp-2 text-fg-muted">
            {getValue<string | null>() ?? <span className="text-fg-muted">—</span>}
          </span>
        ),
      },
      {
        id: 'workflow',
        header: 'Workflow',
        enableSorting: false,
        cell: ({ row }) => {
          const wf = workflowById.get(row.original.workflowId);
          if (!wf) {
            return (
              <span className="font-mono text-xs">{row.original.workflowId.slice(0, 8)}…</span>
            );
          }
          return (
            <span className="inline-flex items-center gap-2">
              {wf.name}
              {wf.publishedVersion === null ? (
                <Badge variant="warning">Not published</Badge>
              ) : (
                <Badge variant="outline">v{wf.publishedVersion}</Badge>
              )}
            </span>
          );
        },
      },
      {
        id: 'portal',
        header: 'Portal',
        enableSorting: false,
        size: 120,
        cell: ({ row }) =>
          row.original.portal.visible ? (
            <Badge variant="success">Visible</Badge>
          ) : (
            <Badge variant="secondary">Hidden</Badge>
          ),
      },
      {
        id: 'actions',
        header: '',
        enableSorting: false,
        size: 100,
        cell: ({ row }) => (
          <div className="flex justify-end gap-1">
            <Button
              variant="ghost"
              size="icon"
              aria-label={`Edit ${row.original.name}`}
              onClick={() => setEditing(row.original)}
            >
              <Pencil />
            </Button>
            <Button
              variant="ghost"
              size="icon"
              aria-label={`Delete ${row.original.name}`}
              onClick={() => setDeleting(row.original)}
              disabled={rows.length <= 1}
            >
              <Trash2 />
            </Button>
          </div>
        ),
      },
    ],
    [rows, workflowById, reorder.isPending],
  );

  return (
    <>
      <DataTable
        columns={columns}
        data={issueTypes.data}
        getRowId={(t) => t.id}
        isLoading={issueTypes.isPending}
        error={issueTypes.error}
        onRetry={() => void issueTypes.refetch()}
        enableSorting={false}
        caption="Issue types"
        emptyTitle="No issue types"
        emptyDescription="Every ticket has an issue type; each type follows one published workflow."
        toolbar={
          <div className="flex justify-end">
            <Button onClick={() => setEditing('new')}>
              <Plus />
              New issue type
            </Button>
          </div>
        }
      />
      <IssueTypeDialog
        project={project}
        workflows={workflows.data ?? []}
        issueType={editing === 'new' ? null : editing}
        open={editing !== null}
        onOpenChange={(open) => {
          if (!open) setEditing(null);
        }}
      />
      <DeleteIssueTypeDialog
        projectId={projectId}
        issueType={deleting}
        others={rows.filter((t) => t.id !== deleting?.id)}
        onOpenChange={(open) => {
          if (!open) setDeleting(null);
        }}
      />
    </>
  );
}
